import { useCallback, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { IUserAvatar } from '@interfaces/avatar';
import { ExperienceFeatures, IEvent } from '@interfaces/event';
import { IUser } from '@interfaces/user';
import { APP_LOGO } from '@components/AppLogo/interfaces';
import { DashboardButtonEnum } from '@constants/dashboard';
import { isEventFeatureEnabled, isSinglePlayerEvent } from '@helpers/events';
import { MytaverseLogger } from '@helpers/logger';
import { CHAT_DRAWER_WIDTH, MINUTES_ALLOW_TO_JOIN } from '@constants';

import { env } from 'env';
import ROUTES from '@constants/routes';

export const useClientSpecificProfileInformation = () => {
  const { user } = useMytaverse();

  const profileUserName = useMemo(
    () => <Typography>{user.name}</Typography>,
    [user],
  );

  const profileEmail = useMemo(() => user.email, [user]);

  const profileGender = '';

  return { profileUserName, profileEmail, profileGender };
};

export const useClientSpecificVirtualAssistant = (): {
  icon: JSX.Element;
  label: string;
} | null => {
  return null;
};

export const useClientSpecificHasMultiplayerTag = () => {
  return { hasMultiplayerTag: false };
};

export const useClientSpecificPrivateChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isPrivateChatEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.PrivateChat),
    [currentEvent],
  );

  return { isPrivateChatEnabled };
};

export const useClientSpecificBroadcastChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isBroadcastEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Broadcast),
    [currentEvent],
  );

  return { isBroadcastEnabled };
};

export const useClientSpecificGlobalChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isGlobalChatEnabled = useMemo(() => {
    return isEventFeatureEnabled(currentEvent, ExperienceFeatures.GlobalChat);
  }, [currentEvent]);

  return { isGlobalChatEnabled };
};

export const useClientSpecificCameraVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isCameraEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Camera),
    [currentEvent],
  );

  return { isCameraEnabled };
};

export const useClientSpecificMicVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMicEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Mic),
    [currentEvent],
  );

  return { isMicEnabled };
};

export const useClientSpecificParticipantsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isParticipantsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Participants),
    [currentEvent],
  );

  return { isParticipantsEnabled };
};

export const useClientSpecificOnlineUsersVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isOnlineUsersEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.OnlineUsers),
    [currentEvent],
  );

  return { isOnlineUsersEnabled };
};

export const useClientSpecificTeleportVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isTeleportFeatureEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Teleport),
    [currentEvent],
  );

  return { isTeleportFeatureEnabled };
};

export const useClientSpecificLaserPointerVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isLaserPointerEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.LaserPointer),
    [currentEvent],
  );

  return { isLaserPointerEnabled };
};

export const useClientSpecificMagnifierVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMagnifierEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Magnifier),
    [currentEvent],
  );

  return { isMagnifierEnabled };
};

export const useClientSpecificEmojisVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isEmojisEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Emojis),
    [currentEvent],
  );

  return { isEmojisEnabled };
};

export const useClientSpecificReactionsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isReactionsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Reactions),
    [currentEvent],
  );

  return { isReactionsEnabled };
};

export const useClientSpecificMapVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMapEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Map),
    [currentEvent],
  );

  return { isMapEnabled };
};

export const useClientSpecificFollowMeVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isFollowMeEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.FollowMe),
    [currentEvent],
  );

  return { isFollowMeEnabled };
};

export const useClientSpecificBroadcastChatInputVisibility = () => {
  return { canBroadcast: true };
};

export const useClientSpecificChatDrawerWidth = () => {
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();

  if (isPrivateChatEnabled && isBroadcastEnabled) {
    return { chatDrawerWidth: CHAT_DRAWER_WIDTH.THREE_TABS };
  }

  return { chatDrawerWidth: CHAT_DRAWER_WIDTH.TWO_TABS };
};

export const useClientSpecificCarouselEventsTransform = () => {
  return useCallback((events: IEvent[]) => events, []);
};

export const useClientSpecificJoinEventSchedulerMinToJoin = () => {
  return MINUTES_ALLOW_TO_JOIN;
};

export const useClientSpecificCanCreateNewAvatar = (
  listOfAvatarsAlreadyCreated: IUserAvatar[],
): boolean => {
  return (
    listOfAvatarsAlreadyCreated.length <
    parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '3')
  );
};

export const useClientSpecificCustomTextDeleteEditActionsOnAvatar = (
  _avatar: IUserAvatar | null,
) => {
  return {
    canEdit: true,
    canDelete: true,
    cannotEditAvatarNotification: undefined as string | undefined,
    customDeleteAvatarTitle: '',
    customDeleteAvatarText: '',
  };
};

export const useClientSpecificHideGenericAvaturnType = () => {
  return false;
};

export const useClientSpecificHideRevokeAvatarConsentLink = () => {
  return false;
};

export const useClientSpecificHideAvaturnCreation = () => {
  return false;
};

export const useClientSpecificHideAvatarPolicyModal = () => {
  return false;
};

export const useClientSpecificNoEventsNotification = () => {
  return false;
};

export const useClientSpecificAvatarDeletion = () => {
  return useCallback(async (_avatar: IUserAvatar) => {
    return;
  }, []);
};

export const useClientSpecificLogo = () => {
  return {
    logo: APP_LOGO.DEFAULT,
    shouldStickToTheTop: false,
  };
};

export const useClientSpecificDashboardButtonDisabled = (
  _button: DashboardButtonEnum,
) => {
  return false;
};

export const useClientSpecificUserAvatars = (avatars: IUserAvatar[]) => {
  return useMemo(() => avatars, [avatars]);
};

export const useClientSpecificEventProvider = () => {
  return (Component: JSX.Element) => Component;
};

const resolveSelectedAvatar = (user: IUser): IUserAvatar | null => {
  let selectedAvatar =
    user?.activeAvatarId &&
    user?.avatars?.find((avatar) => avatar.avatarId === user.activeAvatarId);

  if (!selectedAvatar && user?.avatars?.length > 0) {
    MytaverseLogger.log(
      'Active avatar not resolved, falling back to first avatar',
    );
    selectedAvatar = user?.avatars[0];
  }

  if (!selectedAvatar && user?.defaultAvatar?.avatarUrl) {
    MytaverseLogger.log('User has no active avatars, defaultAvatar is used');
    selectedAvatar = user?.defaultAvatar;
  }

  return selectedAvatar || null;
};

export const useClientSpecificSelectEvent = () => {
  const navigate = useNavigate();

  const { user, setCurrentAvatarId, setCustomAvatarUrl, setUser } =
    useMytaverse();

  return useCallback(
    async (event: IEvent) => {
      if (isSinglePlayerEvent(event)) {
        const selectedAvatar = resolveSelectedAvatar(user);

        if (selectedAvatar) {
          const activeAvatarId = selectedAvatar.avatarId ?? undefined;
          setCurrentAvatarId(activeAvatarId ?? null);
          setCustomAvatarUrl(selectedAvatar.avatarUrl ?? null);
          setUser((prevState: IUser) => ({ ...prevState, activeAvatarId }));
        }

        return navigate(ROUTES.DEVICES_TEST(event.eventId));
      }
      return navigate(ROUTES.SELECT_AVATAR_PROVIDER(event?.eventId));
    },
    [user?.activeAvatarId, user?.avatars, user?.defaultAvatar],
  );
};

export const useClientSpecificDirectEntryAvatarGate = () => {
  const { pathname } = useLocation();

  const {
    user,
    customAvatarUrl,
    setCurrentAvatarId,
    setCustomAvatarUrl,
    setUser,
  } = useMytaverse();
  const { currentEventId } = useMytaverseEvent();

  useEffect(() => {
    if (
      !user ||
      !currentEventId ||
      customAvatarUrl ||
      pathname !== ROUTES.EVENT_PAGE(currentEventId)
    ) {
      return;
    }

    const selectedAvatar = resolveSelectedAvatar(user);

    if (!selectedAvatar?.avatarUrl) {
      return;
    }

    const activeAvatarId = selectedAvatar.avatarId ?? null;
    setCurrentAvatarId(activeAvatarId);
    setCustomAvatarUrl(selectedAvatar.avatarUrl);
    setUser((prevState: IUser) => ({ ...prevState, activeAvatarId }));
  }, [user, currentEventId, customAvatarUrl, pathname]);
};

export const useClientSpecificDeviceTestBackButton = () => {
  const navigate = useNavigate();
  const { currentEvent, currentEventId, currentSkin } = useMytaverseEvent();

  return useCallback(() => {
    const defaultNavigatePage = currentSkin
      ? ROUTES.SELECT_AVATAR_SKIN(currentEventId)
      : ROUTES.SELECT_AVATAR_PROVIDER(currentEventId);

    if (isSinglePlayerEvent(currentEvent)) {
      return navigate(ROUTES.SELECT_EVENT);
    }
    return navigate(defaultNavigatePage);
  }, [currentEventId]);
};
